import { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import clsx from 'clsx';

/**
 * SearchInput - Reusable search component with debounce
 * 
 * @param {string} value - Controlled value
 * @param {function} onChange - Callback when value changes (debounced)
 * @param {function} onSearch - Callback when search is triggered (Enter key)
 * @param {string} placeholder - Placeholder text
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300)
 * @param {boolean} isLoading - Show loading spinner
 * @param {boolean} autoFocus - Auto focus on mount
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {string} className - Additional classes
 * @param {boolean} showClearButton - Show clear button when value exists
 * @param {boolean} fullWidth - Take full width of container
 */
export default function SearchInput({
    value: controlledValue,
    onChange,
    onSearch,
    placeholder = 'Ara...',
    debounceMs = 300,
    isLoading = false,
    autoFocus = false,
    size = 'md',
    className = '',
    showClearButton = true,
    fullWidth = false,
}) {
    const [internalValue, setInternalValue] = useState(controlledValue || '');
    const inputRef = useRef(null);
    const debounceRef = useRef(null);

    // Sync with controlled value
    useEffect(() => {
        if (controlledValue !== undefined) {
            setInternalValue(controlledValue);
        }
    }, [controlledValue]);

    // Debounced onChange
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            if (onChange && internalValue !== controlledValue) {
                onChange(internalValue);
            }
        }, debounceMs);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [internalValue, debounceMs, onChange, controlledValue]);

    const handleInputChange = (e) => {
        setInternalValue(e.target.value);
    };

    const handleClear = () => {
        setInternalValue('');
        onChange?.('');
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && onSearch) {
            onSearch(internalValue);
        }
        if (e.key === 'Escape') {
            handleClear();
        }
    };

    const sizeClasses = {
        sm: {
            container: 'h-8',
            input: 'text-xs pl-8 pr-8',
            icon: 'w-3.5 h-3.5 left-2.5',
            clearIcon: 'w-3.5 h-3.5 right-2',
        },
        md: {
            container: 'h-10',
            input: 'text-sm pl-10 pr-10',
            icon: 'w-4 h-4 left-3',
            clearIcon: 'w-4 h-4 right-3',
        },
        lg: {
            container: 'h-12',
            input: 'text-base pl-12 pr-12',
            icon: 'w-5 h-5 left-4',
            clearIcon: 'w-5 h-5 right-4',
        },
    };

    const sizeConfig = sizeClasses[size];

    return (
        <div
            className={clsx(
                'relative group',
                fullWidth ? 'w-full' : 'w-full sm:w-64 md:w-72 lg:w-80',
                sizeConfig.container,
                className
            )}
        >
            {/* Search Icon */}
            <Search
                className={clsx(
                    'absolute top-1/2 -translate-y-1/2 text-text-muted transition-colors',
                    'group-focus-within:text-primary',
                    sizeConfig.icon
                )}
            />

            {/* Input */}
            <input
                ref={inputRef}
                type="text"
                value={internalValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                autoFocus={autoFocus}
                className={clsx(
                    'w-full h-full rounded-xl',
                    'bg-bg-tertiary border border-border-light',
                    'text-text-primary placeholder:text-text-muted',
                    'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary',
                    'transition-all duration-200',
                    'hover:border-border-medium',
                    sizeConfig.input
                )}
            />

            {/* Clear Button or Loading Spinner */}
            <div className={clsx('absolute top-1/2 -translate-y-1/2', sizeConfig.clearIcon)}>
                {isLoading ? (
                    <Loader2 className="w-full h-full text-text-muted animate-spin" />
                ) : (
                    showClearButton && internalValue && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="p-0.5 rounded-full text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
                            aria-label="Temizle"
                        >
                            <X className="w-full h-full" />
                        </button>
                    )
                )}
            </div>
        </div>
    );
}
